import { useEffect, useMemo, useState } from 'react';
import { Banner, BannerItem } from '@athena/forge';
import { useMutationObserver } from '../../hooks/useMutationObserver';
import { I18N_MAPPING } from '../../constants';
import { useWidgetContext } from '../context/WidgetContext';
import { isAthenaOneWebClient } from '../../utils/app-utils';
import InvalidUsernameOrPasswordBanner from '../primary-auth/InvalidUsernameOrPasswordBanner';
import UserLockedOutBanner from '../primary-auth/UserLockedOutBanner';
import {
  closeOktaSession,
  isPasswordExpiredController,
  isPrimaryAuthController,
  navigateToPrimaryAuthWithError
} from '../../utils/widget-facade';
import TerminalView from '../terminal/TerminalView';
import { getLocalizedString } from '../../utils/i18n-utils';

interface ErrorState {
  criticalErrorMessage: string;
  attentionErrorMessage: string;
}

interface ErrorContainerProps {
  oktaErrorContainer: JQuery;
  renderTerminalView: boolean;
  backToLoginOktaFooterButton?: JQuery;
}

export default function ErrorContainer({
  oktaErrorContainer,
  renderTerminalView,
  backToLoginOktaFooterButton,
}: ErrorContainerProps) {
  function calcErrorState(oktaErrorContainer: JQuery): ErrorState {
    const attentionError = oktaErrorContainer.find('.okta-form-infobox-warning');
    const criticalError = oktaErrorContainer.find('.okta-form-infobox-error').length
      ? oktaErrorContainer.find('.okta-form-infobox-error')
      : oktaErrorContainer.find('.infobox-error');
    return {
      criticalErrorMessage: criticalError.text(),
      attentionErrorMessage: attentionError.find('p').text(),
    };
  }

  function getAlertType(errorState: ErrorState) {
    return errorState.attentionErrorMessage ? 'attention' : 'critical';
  }

  const [ errorState, setErrorState ] = useState(calcErrorState(oktaErrorContainer));
  const { criticalErrorMessage, attentionErrorMessage } = errorState;

  const {
    settings: { clientId },
    controller,
  } = useWidgetContext();
  const displayInvalidUsernameOrPasswordBanner =
    criticalErrorMessage === I18N_MAPPING['en']['errors.E0000004'] && clientId && isAthenaOneWebClient();
  const displayUserLockedOutBanner =
    criticalErrorMessage === I18N_MAPPING['en']['errors.E0000119'] && clientId && isAthenaOneWebClient();
  const useMutationArgs = useMemo(() => {
    return {
      filterOptions: { childList: true },
      onMutation: () => {
        setErrorState(calcErrorState(oktaErrorContainer));
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = oktaErrorContainer.closest('form');
  useEffect(() => {
    form.on('reset', () => {
      setErrorState({ criticalErrorMessage: '', attentionErrorMessage: '' });
    });
  }, [ form ]);

  useMutationObserver({
    element: oktaErrorContainer[0],
    ...useMutationArgs,
  });

  // UIAMUA-7254： Adding 'Reload Option'as an Interim solution
  useEffect(() => {
    if (
      criticalErrorMessage === 'Conflicting App User IDs specified on state token' ||
      criticalErrorMessage === 'No enrollment found matching selection'
    ) {
      const handleReload = async () => {
        await closeOktaSession();
        window.location.reload();
      };
      handleReload();
    }
  }, [ criticalErrorMessage ]);

  function returnBannerComponent() {
    if (displayUserLockedOutBanner) {
      return <>{<UserLockedOutBanner />}</>;
    }
    else if (displayInvalidUsernameOrPasswordBanner) {
      return <>{<InvalidUsernameOrPasswordBanner />}</>;
    }
    else if (criticalErrorMessage) {
      return (
        <>
          {criticalErrorMessage && (
            <Banner alertType={'critical'} className="fe_u_padding--small">
              <BannerItem>{criticalErrorMessage}</BannerItem>
            </Banner>
          )}
        </>
      );
    }
  }

  if (
    [
      getLocalizedString('errors.E0000005'),
      getLocalizedString('errors.E0000011'),
      getLocalizedString('idx.session.expired'),
    ].some((errMessage) => criticalErrorMessage.includes(errMessage)) &&
    !isPrimaryAuthController(controller) &&
    //For symantec prompt errors we don't want to navigate to primary auth
    !isPasswordExpiredController(controller)
  ) {
    navigateToPrimaryAuthWithError(getLocalizedString('errors.E0000005'));
  }
  else if (renderTerminalView) {
    return (
      <TerminalView
        errorMessage={criticalErrorMessage || attentionErrorMessage}
        backToLoginOktaFooterButton={backToLoginOktaFooterButton}
        alertType={getAlertType(errorState)}
      />
    );
  }
  else if (criticalErrorMessage || attentionErrorMessage) {
    return (
      <div className="fe_u_margin--bottom-medium">
        {returnBannerComponent()}
        {criticalErrorMessage && attentionErrorMessage && <div className="fe_u_margin--top-small"></div>}
        {attentionErrorMessage && (
          <Banner alertType={'attention'} className="fe_u_padding--small">
            <BannerItem>{attentionErrorMessage}</BannerItem>
          </Banner>
        )}
      </div>
    );
  }
  return <></>;
}
